import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { FiCheckCircle, FiPauseCircle, FiXCircle, FiTrendingUp, FiDollarSign, FiTarget, FiGlobe } from "react-icons/fi";
import { useLanguage } from "../contexts/LanguageContext";
import { channelDisplay } from "../lib/channels";

// Status labels — mirrors DecisionResult so Result and Share read identically.
const SHARE_STATUS_LABELS = {
  en: { Stable: "Stable", Opportunity: "Opportunity", Critical: "Critical",
        Excellent: "Excellent", Good: "Good", Fair: "Fair", Poor: "Poor" },
  ar: { Stable: "مستقر", Opportunity: "فرصة", Critical: "حرج",
        Excellent: "ممتاز", Good: "جيد", Fair: "مقبول", Poor: "ضعيف" },
};

const PublicSharePage = () => {
  const { t, isRTL, lang, setLang } = useLanguage();
  const [searchParams] = useSearchParams();
  const { slug: slugParam } = useParams();                       // /share/c/:slug
  const querySlug = searchParams.get("slug");                    // /share?slug=xxx (fallback)
  const slug = slugParam || querySlug;
  const [decision, setDecision] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Apply lang from URL param on first load only
{/* <p className="text-gray-300 leading-relaxed">
  {lang === 'ar' ? (decision.reasonAr || decision.reason) : decision.reason}
</p> */}

  useEffect(() => {
    const urlLang = searchParams.get("lang");
    if (urlLang === "ar" || urlLang === "en") {
      setLang(urlLang);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty — run once on mount

  useEffect(() => {
    if (slug) loadSharedDecision();
    else { setError(t("link_invalid")); setLoading(false); }
  }, [slug]);

  const loadSharedDecision = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`https://us-central1-nexusflow-dev.cloudfunctions.net/getSharedDecision?slug=${slug}`);
      setDecision(response.data.decision);
    } catch (err) {
      setError(err.response?.status === 404 ? t("link_invalid") : err.response?.status === 410 ? t("link_expired") : t("error"));
    } finally { setLoading(false); }
  };

  const getIcon = (d) => d === "CONTINUE" ? <FiCheckCircle className="text-green-400 text-6xl" /> : (d === "ADJUST" || d === "PAUSE") ? <FiPauseCircle className="text-yellow-400 text-6xl" /> : d === "STOP" ? <FiXCircle className="text-red-400 text-6xl" /> : null;
  const getColors = (d) => ({ CONTINUE: { bg: "bg-green-500/20", border: "border-green-500", text: "text-green-400" }, ADJUST: { bg: "bg-yellow-500/20", border: "border-yellow-500", text: "text-yellow-400" }, PAUSE: { bg: "bg-yellow-500/20", border: "border-yellow-500", text: "text-yellow-400" }, STOP: { bg: "bg-red-500/20", border: "border-red-500", text: "text-red-400" } }[d] || { bg: "bg-gray-500/20", border: "border-gray-500", text: "text-gray-400" });

  if (loading) return (
    <div  className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
      <div className="text-blue-400 text-xl">{t("loading_decision")}</div>
    </div>
  );

  if (error) return (
    <div  className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8 max-w-md text-center">
        <div className="bg-red-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"><FiXCircle className="text-red-400 text-3xl" /></div>
        <h2 className="text-2xl font-bold text-white mb-2">{t("link_not_available")}</h2>
        <p className="text-gray-400">{error}</p>
      </div>
    </div>
  );

  if (!decision) return null;

  const colors = getColors(decision.decision);

  // Currency: ر.س for Arabic, SAR for English (was hardcoded "$").
  const cur = lang === "ar" ? "ر.س" : "SAR";

  // ── G1/G2/G3 — prefer backend display fields, fall back to legacy kpis ──
  const ICONS = {
    roas:          <FiTrendingUp className="text-blue-400" />,
    profit:        <FiDollarSign className="text-green-400" />,
    cpl:           <FiTarget className="text-purple-400" />,
    cac:           <FiDollarSign className="text-orange-400" />,
    cpa:           <FiTarget className="text-pink-400" />,
    ctr:           <FiTrendingUp className="text-green-400" />,
    cpc:           <FiDollarSign className="text-yellow-400" />,
    customers:     <FiTarget className="text-blue-400" />,
    conversionPct: <FiTrendingUp className="text-teal-400" />,
  };
  const fmtUnit = (row) => {
    if (typeof row.value !== "number") return String(row.value);
    if (row.unit === "x")   return `${row.value.toFixed(2)}x`;
    if (row.unit === "%")   return `${row.value.toFixed(1)}%`;
    if (row.unit === "SAR") return `${row.value.toFixed(2)} ${cur}`;
    if (row.key === "customers") return `${Math.round(row.value)}`;
    return `${row.value}`;
  };

  // Legacy fallback map (older shared decisions with no kpisDisplay)
  const legacyMap = {
    roas: { label: "ROAS", fmt: v => `${v.toFixed(2)}x` },
    profit: { label: t("profit"), fmt: v => `${v.toFixed(2)} ${cur}` },
    cpl: { label: "CPL", fmt: v => `${v.toFixed(2)} ${cur}` },
    cac: { label: "CAC", fmt: v => `${v.toFixed(2)} ${cur}` },
    conversion_rate: { label: t("conversion_rate"), fmt: v => `${v.toFixed(1)}%` },
  };

  const displayKpis = Array.isArray(decision.kpisDisplay) && decision.kpisDisplay.length > 0
    ? decision.kpisDisplay.map(k => ({ key: k.key, label: lang === "ar" ? k.labelAr : k.label, value: k.value, unit: k.unit || "", fromBackend: true }))
    : (decision.kpis
        ? Object.entries(decision.kpis)
            .filter(([k, v]) => legacyMap[k] && v !== null && v !== undefined && !Number.isNaN(v))
            .map(([k, v]) => ({ key: k, label: legacyMap[k].label, value: v, fmt: legacyMap[k].fmt, fromBackend: false }))
        : []);

  const pathBadge     = decision.pathBadge ? (lang === "ar" ? decision.pathBadge.labelAr : decision.pathBadge.label) : null;
  const inputsSummary = Array.isArray(decision.inputsSummary)
    ? decision.inputsSummary.map(i => ({ label: lang === "ar" ? i.labelAr : i.label, value: i.value, unit: i.unit || "" }))
    : [];
  const actions = (lang === "ar" && decision.nextActionsAr?.length > 0) ? decision.nextActionsAr : (decision.nextActions || []);

  return (
    <div  className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black p-4 md:p-8">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-8">
        <div className={`flex items-center justify-between `}>
          <div className={""}>
            <h1 className="text-2xl font-bold text-white mb-1">{t("share_page_title")}</h1>
            <p className="text-gray-400 text-sm">{t("campaign_evaluation_report")}</p>
          </div>
          <div className={`flex items-center gap-3 `}>
            {/* Language toggle */}
            <button
              onClick={() => setLang(lang === "en" ? "ar" : "en")}
              className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 border border-gray-600 text-gray-300 px-3 py-2 rounded-xl text-sm transition-colors"
            >
              <FiGlobe className="text-blue-400" />
              {lang === "en" ? "العربية" : "English"}
            </button>
            <a href="https://getnexusflow.com" target="_blank" rel="noopener noreferrer"
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-sm transition-colors">
              {t("get_nexusflow")}
            </a>
          </div>
        </div>
      </div>

      {/* Decision card */}
      <div className="max-w-4xl mx-auto bg-gray-800 border border-gray-700 rounded-2xl p-8">
        {/* Campaign info */}
        {decision.campaign && (
          <div className={`mb-8 pb-8 border-b border-gray-700 ${""}`}>
            <h2 className="text-xl font-semibold text-white mb-2">{decision.campaign.name}</h2>
            {decision.campaign.channel && <p className="text-gray-400">{t("channel")}: <span className="text-white">{channelDisplay(decision.campaign.channel, lang)}</span></p>}
          </div>
        )}

        {/* Result */}
        <div className="text-center mb-8">
          <div className={`${colors.bg} ${colors.border} border-2 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-4`}>{getIcon(decision.decision)}</div>
          <h2 className={`text-4xl font-bold ${colors.text} mb-2`}>{t(`decision_label_${decision.decision?.toLowerCase()}`) || decision.decision}</h2>
          <p className="text-gray-400 text-lg">{(SHARE_STATUS_LABELS[lang] || SHARE_STATUS_LABELS.en)[decision.status] || decision.status}</p>
          {pathBadge && (
            <div className="mt-3 inline-flex items-center gap-1 bg-blue-500/15 text-blue-300 border border-blue-500/40 px-3 py-1 rounded-full text-sm">
              <FiTarget className="text-xs" />{pathBadge}
            </div>
          )}
          {decision.kpis?.profitWarning && (
            <div className="mt-3 ml-2 inline-flex items-center bg-amber-500/15 text-amber-300 border border-amber-500/40 px-3 py-1 rounded-full text-sm">
              {lang === "ar" ? "العائد فقط (بدون هامش)" : "ROAS only (no margin)"}
            </div>
          )}
        </div>

        {/* Inputs Summary (G3) */}
        {inputsSummary.length > 0 && (
          <div className="mb-8 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs bg-gray-700/30 border border-gray-700 rounded-xl px-4 py-3 text-gray-400">
            {inputsSummary.map((it, idx) => (
              <span key={idx} className="inline-flex items-center gap-1">
                <span>{it.label}:</span>
                <span className="font-medium text-gray-200">{it.value}{it.unit ? ` ${it.unit === "SAR" ? cur : it.unit}` : ""}</span>
                {idx < inputsSummary.length - 1 && <span className="text-gray-600">|</span>}
              </span>
            ))}
          </div>
        )}

        {/* KPIs (G2) */}
        {displayKpis.length > 0 && (
          <div className="mb-8">
            <h3 className="text-white font-semibold mb-4">{t("performance_metrics")}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {displayKpis.map((row) => (
                <div key={row.key} className="bg-gray-700/30 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    {ICONS[row.key] || <FiTrendingUp className="text-blue-400" />}
                    <p className="text-gray-400 text-sm">{row.label}</p>
                  </div>
                  <p className={`text-2xl font-bold ${typeof row.value === "number" && row.value < 0 ? "text-red-400" : "text-white"}`}>
                    {row.fromBackend ? fmtUnit(row) : row.fmt(row.value)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Analysis */}
        <div className="mb-8">
          <h3 className="text-white font-semibold mb-3">{t("analysis")}</h3>
          <p className="text-gray-300 leading-relaxed">
            {lang === 'ar' ? (decision.reasonAr || decision.reason) : decision.reason}
          </p>
        </div>

        {/* Actions */}
        {actions.length > 0 && (
          <div className="mb-8">
            <h3 className="text-white font-semibold mb-3">{t("recommended_actions")}</h3>
            <ul className="space-y-2">
              {actions.map((a, i) => (
                <li key={i} className="text-gray-300 flex items-start gap-3">
                  <span className="text-blue-400 font-bold mt-1">•</span><span>{a}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Next review */}
        {decision.nextReviewDays && (
          <div className="bg-gray-700/30 p-4 rounded-xl mb-8">
            <p className="text-gray-400 text-sm">{t("next_review")}: <span className="text-white font-semibold">{decision.nextReviewDays} {t("days")}</span></p>
          </div>
        )}

        {/* Date */}
        {(() => {
          const ts = decision.createdAt;
          let d = null;
          if (ts) {
            if (typeof ts === "string" || typeof ts === "number") d = new Date(ts);
            else if (ts._seconds != null) d = new Date(ts._seconds * 1000);   // Firestore TS over JSON
            else if (ts.seconds != null)  d = new Date(ts.seconds * 1000);
          }
          if (!d || isNaN(d.getTime())) return null;   // never show "Invalid Date"
          return (
            <div className="text-center text-gray-500 text-sm">
              {t("generated_on")} {d.toLocaleString(isRTL ? "ar-SA" : "en-US")}
            </div>
          );
        })()}
      </div>

      {/* Footer CTA */}
      <div className="max-w-4xl mx-auto mt-8 text-center">
        <p className="text-gray-400 mb-4">{t("share_footer_cta")}</p>
        <a href="https://getnexusflow.com" target="_blank" rel="noopener noreferrer"
          className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-semibold transition-colors">
          {t("try_nexusflow_free")}
        </a>
      </div>
    </div>
  );
};

export default PublicSharePage;